import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Pagination from "../../../../components/common/ApiPaginationController";
import { GlobalFilter } from "../../../../components/common/GlobalFilter";
import style from "../../../../assets/scss/custom.module.scss";
import { format } from "date-fns";
import { COLUMNS } from "./column";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./mlinvcsmanagetable.scss";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSortAmountUpAlt,
  FaEye
} from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import { buttonName, SearchText } from "../../../../locales/en";
import { ddMMMyyyy } from "../../../../utils/constants/appConstants";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return "";
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const MlinvoiceManagementTable = () => {
  const columns = useMemo(() => COLUMNS, []);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const result = await axios.get(
        "http://13.239.134.41:3030/mlInvoicemange"
      );
      setData(result.data.reverse());
    } catch (error) {
      console.error("Error loading users:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load users.",
        icon: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const [expandedRows, setExpandedRows] = useState([]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setGlobalFilter,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setPageSize
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { globalFilter, pageIndex, pageSize } = state;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FilteredData");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });
    saveAs(dataBlob, "Ml_Invoice_list.xlsx");
  };

  if (loading) {
    return <div>{buttonName.loading}</div>;
  }

  return (
    <>
      <div className="d-flex flex-wrap mt-3 mb-3 justify-content-between search">
        <div className="d-flex MlinvoiceHeading align-items-center justify-content-start mb-2 mb-sm-0">
          <h3>Download File</h3>
          <button className="link-btn" onClick={handleDownload}>
            101723190784all.csv
          </button>
        </div>
        <div className={style.inputFilterBar}>
          <GlobalFilter
            filter={globalFilter}
            setFilter={setGlobalFilter}
            columns={columns}
            placeholder={SearchText}
          />
        </div>
      </div>
      <table
        className={`mt-2 mb-3 salesleadtable platformpartnertable bgless_table`}
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                  >
                    <span>{renderSortIcon(column)}</span>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
                <th>Action</th>
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const isExpanded = expandedRows.includes(row.id);
            return (
              <React.Fragment key={row.id}>
                <tr className="parentrow" {...row.getRowProps()}>
                  <td className="expand-td">
                    <span
                      className="expandbtn cursor-pointer"
                      onClick={() => toggleRow(row.id)}
                    >
                      {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                    </span>
                  </td>
                  {row.cells.map((cell) => (
                    <td key={cell.getCellProps().key} {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                  <td>
                    <ul className="sales-lead-icons">
                      <li>
                        <button type="button" className="delete">
                          <FaEye />
                        </button>
                      </li>
                    </ul>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="childrow colexpand">
                    <td colSpan={columns.length + 2}>
                      <div className="tdbody">
                        {columns.map((column) => (
                          <span
                            key={column.accessor}
                            data-label={column.Header}
                          >
                            {column.accessor === "date"
                              ? format(
                                  new Date(row.original[column.accessor]),
                                  ddMMMyyyy
                                )
                              : String(row.original[column.accessor])}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="d-flex flex-wrap mt-5 mb-5">
        <Pagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
        />
      </div>
    </>
  );
};

export default MlinvoiceManagementTable;
